/**
 * AssetModule — the client-side 3D asset loader (Graphics G0).
 *
 * Wraps the engine's already-wired GLTF loader (`ModelPipeline` — Draco + KTX2 +
 * Meshopt, with locally-hosted decoder paths set in GLTFPipeline.init) with a
 * content-hash cache + registry-driven URL resolution. The client discovers assets
 * ONLY through the `render_asset` registry (it never hardcodes a URL): the row
 * holds a MinIO object key, resolved here to `${ASSET_STORE_BASE}/${key}`.
 *
 * Not a per-frame System — an on-demand load/cache/dispose service (the eviction
 * POLICY is G5). G1 binds these loaded assets to structures/props.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';
import { ModelPipeline } from 'enginex/assets/ModelPipeline.js';
import { shareSceneTextures, disposeAssetScene, textureShareDiag } from '../lib/texture_share.js';
import { BytesLru, BYTES_CACHE_BUDGET } from '../lib/bytes_cache.js';
import { AssetCache } from '../lib/asset_cache.js';
import { atlas } from '../state.js';
import { Logger } from 'enginex/core/Logger.js';
import { ASSET_STORE_BASE } from '../config.js';
import { smoothModelOffThread } from '../lib/smooth_offthread.js';
import { applyDetailSurface } from 'enginex/render/materials/detail/index.js';
import { applyAccumulationLayer } from 'enginex/render/materials/AccumulationLayer.js';
import { toPhysicalMaterial } from '../lib/material_fx.js';
import { createInteriorMappingMaterial } from 'enginex/render/materials/InteriorMapping.js';
import { applyCloudShadows } from '../lib/cloud_shadows.js';
import { uNightFactor } from './SkyModule.js';
import { isGlassMaterialName, roomsForGlass, litRatioForNight, interiorHandles, stage3Stats } from '../lib/stage3_fx.js';
import { accumulationHandles } from '../lib/wetness.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { applySpecularAA } from 'enginex/render/materials/SpecularAA.js';
import { applyAnisotropy } from 'enginex/render/materials/AnisotropyModifier.js';
import { applyRetroreflection } from 'enginex/render/materials/Retroreflection.js';
import { applyGlassTransmission } from 'enginex/render/materials/GlassTransmission.js';
import { detailFilterBand, specularAAVarianceCap, glassTierFor, anisotropyFor, retroFor, specularAAHandles, retroHandles, stage5Stats } from '../lib/stage5_fx.js';
import { capSceneTextures, type RetainedTop } from '../lib/texture_budget.js';
import { queueTextureWarm } from '../lib/pipeline_warm.js';
import { pickTextureTier, textureTiersOptedOut } from '../lib/texture_tiers.js';
import { BundleLoader } from 'enginex/assets/bundle/index.js';
import { buildPackIndex, packRowsVersion, assetPacksOptedOut, type PackRef } from '../lib/asset_packs.js';
import { skinRefFor } from '../lib/lod0_skins.js';
import type { LoadMetrics } from '../lib/lod0_ledger.js';
import { opfsGet, opfsPut, fetchCached, opfsDiag, opfsClear } from '../lib/asset_opfs.js';
import { pickRawCodec, rawMemberUri, rawCodecOptedOut, detectCodecSupport, type RawCodec } from '../lib/raw_codec.js';
import { promoteCompressedTexture, demoteCompressedTexture, texturePromotionStats } from 'enginex/render/textures/index.js';

function mipsFirstOptedOut(): boolean { try { return localStorage.getItem('atlas:mips-first') === 'off'; } catch { return false; } }

export class AssetModule implements IModule {
  readonly name = 'AssetModule';
  private loader: ModelPipeline | null = null;
  private app: App | null = null;
  // Shared-aware disposer (S4): textures the registry shares across LOD levels
  // are refcounted and disposed with their LAST scene, never with the first.
  private readonly cache = new AssetCache(disposeAssetScene);
  private readonly envCache = new Map<string, THREE.DataTexture>(); // G3: HDRI envmaps (equirect)
  private readonly textureCache = new Map<string, THREE.Texture>(); // ground/facade textures (direct CDN keys)
  private readonly textureInflight = new Map<string, Promise<THREE.Texture>>();

  // ── Global GLB-load gate (2026-07-21) ─────────────────────────────────────
  // EVERY consumer (settlements, foliage, prefetch, structures) funnels through
  // load()/loadVariant(), so this is the one chokepoint: at area entry dozens
  // of GLBs used to decode concurrently, their texture/geometry sub-jobs
  // competing for the worker pools and the main thread. Four lanes keep the
  // pipe full while letting frames breathe. Single-flight dedup rides along:
  // two settlements loading the same prefab used to fetch+parse it twice.
  private static readonly LOAD_LANES = 4;
  private gateActive = 0;
  private readonly gateQueue: Array<() => void> = [];
  private readonly inflight = new Map<string, Promise<THREE.Object3D>>();

  // ── Texture tiers (settlement study R1, 2026-09-02) ───────────────────────
  // A tier is the same GLB with its KTX2 mip chains truncated at cook time so
  // nothing exceeds the preset's maxTextureRes — the bytes a potato/low client
  // downloads now match what it keeps (it used to fetch the 2048² chain and
  // drop levels at parse). The cache key is the file actually fetched, and
  // `tierKeyFor` remembers it per canonical/variant key so dispose() releases
  // the SAME entry even if the preset changes mid-session.
  private readonly tierKeyFor = new Map<string, string>();
  private readonly tierStats = { picks: new Map<number, number>(), untiered: 0, bytesTiered: 0 };

  /** The tier file to fetch for (asset, lod), or undefined for the untiered file. */
  private textureTier(renderAssetId: bigint, lod: number): { uri: string; maxTextureRes: number; sizeBytes: bigint } | undefined {
    if (!atlas.conn || textureTiersOptedOut()) return undefined;
    const cap = activePreset().maxTextureRes;
    return pickTextureTier(AssetModule.rowsByAsset<{ renderAssetId: bigint; lod: number; uri: string; maxTextureRes: number; sizeBytes: bigint }>(
      (atlas.conn.db as unknown as Record<string, unknown>).renderAssetTextureTier, 'renderAssetTextureTierByAsset', renderAssetId), lod, cap);
  }

  /**
   * Rows of a registry table for one asset: the btree index accessor when the
   * SDK exposes it, else a filtered iteration. Harness pages stub these tables
   * with a bare `iter()` (and some have no tier table at all) — the loader
   * must not depend on the exact SDK surface to stay testable.
   */
  private static rowsByAsset<T extends { renderAssetId: bigint }>(table: unknown, index: string, renderAssetId: bigint): T[] {
    if (!table || typeof table !== 'object') return [];
    const t = table as Record<string, unknown> & { iter?: () => Iterable<T> };
    const idx = t[index] as { filter?: (id: bigint) => Iterable<T> } | undefined;
    if (idx && typeof idx.filter === 'function') return [...idx.filter(renderAssetId)];
    if (typeof t.iter === 'function') return [...t.iter()].filter((x) => x.renderAssetId === renderAssetId);
    return [];
  }

  /** Resolve the cache key + URL for (asset, lod): a texture tier when one fits the preset, else `baseKey`/`baseUri`. */
  private representation(renderAssetId: bigint, lod: number, baseKey: string, baseUri: string): { key: string; uri: string } {
    const tier = this.textureTier(renderAssetId, lod);
    // A tier whose uri IS the base file is the cooker's self row (the source
    // registered at its own res) — the untiered file, keyed as before.
    if (tier && tier.uri !== baseUri) {
      this.tierKeyFor.set(baseKey, tier.uri);
      this.tierStats.picks.set(tier.maxTextureRes, (this.tierStats.picks.get(tier.maxTextureRes) ?? 0) + 1);
      this.tierStats.bytesTiered += Number(tier.sizeBytes);
      return { key: tier.uri, uri: tier.uri };
    }
    this.tierStats.untiered++;
    return { key: this.tierKeyFor.get(baseKey) ?? baseKey, uri: baseUri };
  }

  /** The cache key a previous load acquired for `baseKey` (a tier uri or the base key itself). */
  private acquiredKey(baseKey: string): string { return this.tierKeyFor.get(baseKey) ?? baseKey; }

  // ── Asset packs (settlement study R6, 2026-09-02) ─────────────────────────
  // `asset_pack` rows map member object keys onto one .vpak on the store. A
  // fetch for a member key opens its pack ONCE (one request, single-flight,
  // whole pack buffered) and serves the member as a blob: URL through the
  // same GLTF pipeline. A pack that fails to open falls back to the per-file
  // fetch, and packs are never load-bearing for correctness.
  private packIndex: Map<string, PackRef> | null = null;
  private packIndexVersion = '';
  private readonly openPacks = new Map<string, Promise<BundleLoader | null>>();
  /** L5: the pre-transcoded pack variant this GPU samples directly (null = the Basis files, transcoded in the worker). */
  private rawCodec: RawCodec | null = null;
  readonly rawStats = { codec: '' as string, hits: 0, misses: 0 };
  /** L3: blob URLs of packs served from the persistent cache (revoked never — a pack lives for the session). */
  private readonly packBlobUrls = new Map<string, string>();
  private readonly packStats = { opened: 0, hits: 0, misses: 0, failures: 0, bytes: 0, lastError: '' };

  /** The pack (name, uri) serving `memberUri`, or undefined when it is an individual object. */
  private packFor(memberUri: string): PackRef | undefined {
    const conn = atlas.conn;
    if (!conn || assetPacksOptedOut()) return undefined;
    const table = (conn.db as unknown as Record<string, unknown>).assetPack as { iter?: () => Iterable<{ name: string; uri: string; members: string }> } | undefined;
    if (!table || typeof table.iter !== 'function') return undefined; // harness stubs / older schema: no packs
    const rows = [...table.iter()];
    const version = packRowsVersion(rows);
    if (!this.packIndex || version !== this.packIndexVersion) {
      this.packIndex = buildPackIndex(rows);
      this.packIndexVersion = version;
    }
    return this.packIndex.get(memberUri);
  }

  /** Open (once) the pack at `uri`; null when it cannot be opened (the caller fetches the file itself). */
  private openPack(pack: { name: string; uri: string }): Promise<BundleLoader | null> {
    let p = this.openPacks.get(pack.uri);
    if (!p) {
      // L3: a pack comes from the persistent cache when it has been seen before
      // (one OPFS read instead of a 10–40 MB download), else from the network
      // and into the cache; the loader reads it through a blob URL either way.
      p = fetchCached(`${ASSET_STORE_BASE}/${pack.uri}`, pack.uri)
        .then((bytes) => { const blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' })); this.packBlobUrls.set(pack.uri, blobUrl); return BundleLoader.open(blobUrl, { eager: true, mount: pack.name }); })
        .then((b) => { this.packStats.opened++; this.packStats.bytes += b.stats().storedBytes; return b; })
        .catch((err: unknown) => { this.packStats.failures++; this.packStats.lastError = `${pack.name}: ${String((err as Error)?.message ?? err)}`.slice(0, 200); Logger.warn('AssetModule', `pack ${pack.name} failed to open, falling back to per-file fetch: ${String(err)}`); return null; });
      this.openPacks.set(pack.uri, p);
    }
    return p;
  }

  /** Resolve a member key to a blob: URL from its pack, or the store URL when unpacked. */
  private async urlFor(memberUri: string): Promise<string> {
    // L5: a raw twin (`raw/<codec>/<member>`) in a registered pack skips the Basis transcoder.
    if (this.rawCodec) {
      const rawUri = rawMemberUri(this.rawCodec, memberUri);
      const rp = this.packFor(rawUri);
      if (rp) {
        const b = await this.openPack(rp);
        if (b) {
          try { const u = await b.getBlobUrl(rawUri); this.packStats.hits++; this.rawStats.hits++; return u; }
          catch { this.rawStats.misses++; }
        }
      }
    }
    const pack = this.packFor(memberUri);
    if (pack) {
      const b = await this.openPack(pack);
      if (b) {
        try { const u = await b.getBlobUrl(memberUri); this.packStats.hits++; return u; }
        catch { this.packStats.misses++; }
      }
    }
    return `${ASSET_STORE_BASE}/${memberUri}`;
  }

  /** Diagnostics for probes (`__assetPacks()`). */
  assetPackStats(): { enabled: boolean; opened: number; hits: number; misses: number; failures: number; bytesMb: number; packs: number; lastError: string; raw: { codec: string; hits: number; misses: number } } {
    return { enabled: !assetPacksOptedOut(), ...this.packStats, bytesMb: +(this.packStats.bytes / 1048576).toFixed(1), packs: this.packIndex ? new Set([...this.packIndex.values()].map((p) => p.uri)).size : 0, raw: { ...this.rawStats } };
  }

  /** Diagnostics for probes: which tiers were picked and the opt-out state. */
  textureTierStats(): { enabled: boolean; cap: number; picks: Record<string, number>; untiered: number; bytesTieredMb: number } {
    return {
      enabled: !textureTiersOptedOut(), cap: activePreset().maxTextureRes,
      picks: Object.fromEntries([...this.tierStats.picks].map(([k, v]) => [String(k), v])),
      untiered: this.tierStats.untiered, bytesTieredMb: +(this.tierStats.bytesTiered / 1048576).toFixed(1),
    };
  }

  private async gate<T>(work: () => Promise<T>): Promise<T> {
    if (this.gateActive >= AssetModule.LOAD_LANES) {
      await new Promise<void>((r) => this.gateQueue.push(r));
    }
    this.gateActive++;
    try { return await work(); } finally {
      this.gateActive--;
      this.gateQueue.shift()?.();
    }
  }

  /** One network+parse attempt with a single retry — MinIO occasionally drops
   *  a body mid-stream under load (ERR_CONTENT_LENGTH_MISMATCH, probe-caught). */
  /** S8: recently fetched GLB bytes (direct fetches only — pack members already live in the opened .vpak). */
  private readonly bytesCache = new BytesLru(BYTES_CACHE_BUDGET);

  // ── LOD0 shape/skin split (settlement LOD0 research L1 + L2, 2026-09-03) ──
  /** The app's shared KTX2 transcoder (skins are stand-alone KTX2 files). */
  private ktx2: { loadAsync(url: string): Promise<THREE.Texture> } | null = null;
  /** Textures currently promoted, with how (GPU merge, or the CPU full-chain fallback keeping the chain for demotion). */
  private readonly skinTex = new Map<THREE.Texture, { cap: number; mode: 'gpu' | 'cpu'; chain?: unknown[]; base?: { width: number; height: number; levels: number } }>();
  readonly skinStats = { promoted: 0, demoted: 0, failed: 0, cpuFallbacks: 0, bytes: 0, inflight: 0, refsSeen: 0, prefetched: 0, prefetchHits: 0 };
  private static readonly SKIN_SLOTS = ['map', 'normalMap', 'emissiveMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'alphaMap', 'specularMap', 'bumpMap'] as const;

  /** L8: skins decoded ahead of need (a closing approach), consumed by promoteSkins; LRU-bounded. */
  private readonly skinPrefetch = new Map<string, { p: Promise<THREE.Texture | null>; at: number }>();
  private static readonly SKIN_PREFETCH_MAX = 24;
  private static readonly SKIN_PREFETCH_TTL_MS = 90_000;

  /** Decode the skins a master will want at `cap` in the background (scheduler.postTask background priority). */
  prefetchSkins(master: THREE.Object3D, cap: number): number {
    let started = 0;
    for (const t of this.skinnableTextures(master)) {
      const ref = skinRefFor(t.userData, cap);
      if (!ref || this.skinTex.has(t) || this.skinPrefetch.has(ref.uri)) continue;
      const run = (): Promise<THREE.Texture | null> => this.ktx2 ? this.urlFor(ref.uri).then((u) => this.ktx2!.loadAsync(u)).catch(() => null) : Promise.resolve(null);
      const sched = (globalThis as unknown as { scheduler?: { postTask<T>(cb: () => T, o: { priority: string }): Promise<T> } }).scheduler;
      const p: Promise<THREE.Texture | null> = sched?.postTask ? sched.postTask(run, { priority: 'background' }).then((x) => x).catch(() => null) : run();
      this.skinPrefetch.set(ref.uri, { p, at: performance.now() });
      started++;
      this.skinStats.prefetched++;
    }
    // Bound the cache: drop the oldest beyond the cap or the TTL (a prefetch nobody consumed).
    const now = performance.now();
    for (const [uri, e] of [...this.skinPrefetch].sort((a, b) => a[1].at - b[1].at)) {
      if (this.skinPrefetch.size <= AssetModule.SKIN_PREFETCH_MAX && now - e.at < AssetModule.SKIN_PREFETCH_TTL_MS) break;
      this.skinPrefetch.delete(uri);
      void e.p.then((tex) => { try { tex?.dispose(); } catch { /* never uploaded */ } });
    }
    return started;
  }

  /** Distinct textures of a master that carry skin refs (`userData.atlasSkin`, stamped by the shape cook and carried across the S4 share). */
  private skinnableTextures(master: THREE.Object3D): THREE.Texture[] {
    const out = new Set<THREE.Texture>();
    master.traverse((o) => {
      const mats = (o as THREE.Mesh).material;
      for (const mat of Array.isArray(mats) ? mats : mats ? [mats] : []) {
        const rec = mat as unknown as Record<string, unknown>;
        for (const slot of AssetModule.SKIN_SLOTS) {
          const t = rec[slot] as THREE.Texture | null | undefined;
          if (t && (t as { isTexture?: boolean }).isTexture && (t.userData as { atlasSkin?: unknown }).atlasSkin) out.add(t);
        }
      }
    });
    return [...out];
  }

  /**
   * Promote every skinnable texture of a LOD0 shape master to `cap` IN PLACE
   * (the textures are the registry's shared objects, so every level and every
   * site showing them samples the full chain at once). WebGPU: the engine
   * merges the skin's top levels over the resident chain on the GPU (L2);
   * elsewhere the skin + a CPU copy of the resident chain are not both
   * available, so the fallback re-creates the texture from the full tier
   * file's chain (today's upload cost, only inside the touch band).
   */
  async promoteSkins(master: THREE.Object3D, cap: number, fallback?: () => Promise<THREE.Object3D | null>): Promise<{ promoted: number; failed: number; bytes: number }> {
    const native = this.app?.renderer.native as unknown;
    let promoted = 0, failed = 0, bytes = 0;
    let donor: THREE.Object3D | null | undefined;
    const texs = this.skinnableTextures(master);
    this.skinStats.refsSeen += texs.length;
    for (const t of texs) {
      const ref = skinRefFor(t.userData, cap);
      if (!ref) continue;
      const cur = this.skinTex.get(t);
      if (cur && cur.cap >= ref.width) continue;
      this.skinStats.inflight++;
      try {
        let done = false;
        if (this.ktx2 && native) {
          const pre = this.skinPrefetch.get(ref.uri);
          let top: THREE.Texture | null = null;
          if (pre) { this.skinPrefetch.delete(ref.uri); top = await pre.p; if (top) this.skinStats.prefetchHits++; }
          if (!top) { const url = await this.urlFor(ref.uri); top = await this.ktx2.loadAsync(url); }
          top.colorSpace = t.colorSpace;
          const r = promoteCompressedTexture(native, t, top);
          if (r === 'promoted') { done = true; this.skinTex.set(t, { cap: ref.width, mode: 'gpu' }); }
          else if (r === 'already') { done = true; this.skinTex.set(t, { cap: ref.width, mode: 'gpu' }); try { top.dispose(); } catch { /* never uploaded */ } } // shared with a key promoted earlier
          else { try { top.dispose(); } catch { /* never uploaded */ } if (r !== 'unsupported') Logger.debug('Assets', `skin promotion ${r} for ${t.name || 'texture'}`); }
        }
        if (!done && fallback) {
          // CPU fallback (WebGL2 / Compatibility Mode): the full-chain tier file, matched by traversal order.
          if (donor === undefined) donor = await fallback();
          const d = donor ? this.matchDonorTexture(master, donor, t) : null;
          const dm = d as unknown as { mipmaps?: unknown[]; image?: { width: number; height: number } } | null;
          if (dm?.mipmaps && dm.mipmaps.length > 0 && dm.image) {
            const ct = t as unknown as { mipmaps: unknown[]; image: { width: number; height: number }; needsUpdate: boolean };
            const base = { width: ct.image.width, height: ct.image.height, levels: ct.mipmaps.length };
            ct.mipmaps = dm.mipmaps.slice();
            ct.image = { width: dm.image.width, height: dm.image.height };
            t.dispose(); ct.needsUpdate = true; // the renderer re-creates the GPU texture from the CPU chain
            this.skinTex.set(t, { cap: ref.width, mode: 'cpu', chain: ct.mipmaps, base });
            this.skinStats.cpuFallbacks++; done = true;
          }
        }
        if (done) { promoted++; bytes += ref.bytes; this.skinStats.promoted++; } else { failed++; this.skinStats.failed++; }
      } catch (e) {
        failed++; this.skinStats.failed++;
        Logger.warn('Assets', `skin ${ref.uri} failed: ${(e as Error).message ?? e}`);
      } finally { this.skinStats.inflight--; }
    }
    this.skinStats.bytes += bytes;
    if (donor) this.disposeDonorScene(donor);
    return { promoted, failed, bytes };
  }

  /** The donor scene's texture at the same (material order, slot) as `t` in the shape master. */
  private matchDonorTexture(master: THREE.Object3D, donor: THREE.Object3D, t: THREE.Texture): THREE.Texture | null {
    const shapeMats: THREE.Material[] = [], donorMats: THREE.Material[] = [];
    const collect = (root: THREE.Object3D, into: THREE.Material[]): void => root.traverse((o) => { const m = (o as THREE.Mesh).material; for (const mat of Array.isArray(m) ? m : m ? [m] : []) if (!into.includes(mat)) into.push(mat); });
    collect(master, shapeMats); collect(donor, donorMats);
    if (shapeMats.length !== donorMats.length) return null;
    for (let i = 0; i < shapeMats.length; i++) {
      const a = shapeMats[i] as unknown as Record<string, unknown>, b = donorMats[i] as unknown as Record<string, unknown>;
      for (const slot of AssetModule.SKIN_SLOTS) if (a[slot] === t) return (b[slot] as THREE.Texture | null | undefined) ?? null;
    }
    return null;
  }

  /** Swap every promoted texture of a master back to its base chain. Returns how many were demoted. */
  demoteSkins(master: THREE.Object3D): number {
    const native = this.app?.renderer.native as unknown;
    let n = 0;
    for (const t of this.skinnableTextures(master)) {
      const cur = this.skinTex.get(t);
      if (!cur) continue;
      if (cur.mode === 'gpu') { if (native && demoteCompressedTexture(native, t)) n++; }
      else if (cur.chain && cur.base) {
        const ct = t as unknown as { mipmaps: unknown[]; image: { width: number; height: number }; needsUpdate: boolean };
        ct.mipmaps = cur.chain.slice(cur.chain.length - cur.base.levels);
        ct.image = { width: cur.base.width, height: cur.base.height };
        t.dispose(); ct.needsUpdate = true; n++;
      }
      this.skinTex.delete(t);
    }
    this.skinStats.demoted += n;
    return n;
  }

  /** L3 diagnostics (`__assetCache()`): persistent-cache hits/misses/bytes; `clear()` empties it (probe lever). */
  assetCacheStats(): Promise<Awaited<ReturnType<typeof opfsDiag>>> { return opfsDiag(); }
  clearAssetCache(): Promise<boolean> { return opfsClear(); }

  /** Diagnostics for probes (`__assetSkins()`). */
  assetSkinStats(): typeof this.skinStats & { resident: number; engine: typeof texturePromotionStats } {
    return { ...this.skinStats, resident: this.skinTex.size, engine: texturePromotionStats };
  }

  /**
   * The CPU-fallback donor: the full-chain tier file at `cap`, parsed RAW —
   * no texture sharing (the registry would hand back the resident objects,
   * whose CPU chains are stripped), no warm, no cache. Single use; dispose
   * with `disposeDonorScene` once its chains have been adopted.
   */
  /**
   * A GLB by URL, outside the registry (the static side-channels: the
   * ceremony props under public/props/). One parsed master per URL for the
   * session (callers clone); the same KTX2/meshopt-wired loader as the
   * registry path, with the tier's texture prep. Throws on a failed fetch.
   */
  async loadUrl(url: string): Promise<THREE.Object3D> {
    if (!this.loader) throw new Error('AssetModule not initialized');
    const have = this.urlMasters.get(url);
    if (have) return have;
    const p = (async () => {
      const scene = await this.parseUrl(url);
      shareSceneTextures(scene);
      this.tierPrep(scene);
      return scene;
    })();
    this.urlMasters.set(url, p);
    try { return await p; } catch (e) { this.urlMasters.delete(url); throw e; }
  }
  private readonly urlMasters = new Map<string, Promise<THREE.Object3D>>();

  async loadDonorScene(renderAssetId: bigint, cap: number): Promise<THREE.Object3D | null> {
    const row = this.resolve(renderAssetId);
    if (!row || !this.loader) return null;
    const tier = atlas.conn && !textureTiersOptedOut()
      ? pickTextureTier(AssetModule.rowsByAsset<{ renderAssetId: bigint; lod: number; uri: string; maxTextureRes: number; sizeBytes: bigint }>(
        (atlas.conn.db as unknown as Record<string, unknown>).renderAssetTextureTier, 'renderAssetTextureTierByAsset', renderAssetId), 0, cap)
      : undefined;
    const uri = tier ? tier.uri : row.sourceUri;
    try { return await this.gate(() => this.fetchScene(uri)); } catch { return null; }
  }
  disposeDonorScene(scene: THREE.Object3D): void {
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      for (const mat of Array.isArray(m.material) ? m.material : m.material ? [m.material] : []) mat.dispose();
    });
  }

  /** L9: the last fetch+parse metrics per member uri (taken once by the caller that turns them into a ledger entry). */
  private readonly loadMetrics = new Map<string, LoadMetrics>();
  takeLoadMetrics(uri: string): LoadMetrics | undefined { const m = this.loadMetrics.get(uri); this.loadMetrics.delete(uri); return m; }
  private static readonly NO_METRICS: LoadMetrics = { source: 'cache', wireBytes: 0, fetchMs: 0, parseMs: 0 };

  private async fetchScene(memberUri: string): Promise<THREE.Object3D> {
    const t0 = performance.now();
    const url = await this.urlFor(memberUri);
    if (url.startsWith('blob:')) { // pack-served: in memory already
      const t1 = performance.now();
      const scene = await this.parseUrl(url);
      this.loadMetrics.set(memberUri, { source: 'pack', wireBytes: 0, fetchMs: +(t1 - t0).toFixed(1), parseMs: +(performance.now() - t1).toFixed(1) });
      return scene;
    }
    // Direct fetch: serve the bytes from the LRU when we have them (a level
    // evicted and reloaded parses from memory instead of the network), else
    // fetch once and keep them.
    let bytes = this.bytesCache.get(url);
    let source: LoadMetrics['source'] = 'memory';
    if (!bytes) {
      // L3: the persistent cache (OPFS) before the network; a network fetch is stored for the next visit.
      const disk = await opfsGet(memberUri);
      if (disk) { bytes = disk; source = 'cache'; }
      else {
        source = 'network';
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch ${res.status} ${memberUri}`);
        bytes = await res.arrayBuffer();
        opfsPut(memberUri, bytes);
      }
      this.bytesCache.put(url, bytes);
    }
    const t1 = performance.now();
    const blob = URL.createObjectURL(new Blob([bytes], { type: 'model/gltf-binary' }));
    try {
      const scene = await this.parseUrl(blob);
      this.loadMetrics.set(memberUri, { source, wireBytes: bytes.byteLength, fetchMs: +(t1 - t0).toFixed(1), parseMs: +(performance.now() - t1).toFixed(1) });
      return scene;
    } finally { URL.revokeObjectURL(blob); }
  }

  private async parseUrl(url: string): Promise<THREE.Object3D> {
    // Versona rig wave (2026-09-05): a rigged body's clips ride the GLTF result,
    // not its scene — carry them on the scene (Object3D.animations, copied by
    // clone/SkeletonUtils.clone) or every rig arrives mute.
    const keep = (g: { scene: THREE.Object3D; animations?: THREE.AnimationClip[] }): THREE.Object3D => { g.scene.animations = g.animations ?? []; return g.scene; };
    try {
      return keep(await this.loader!.loadAs(url, 'gltf') as { scene: THREE.Object3D; animations?: THREE.AnimationClip[] });
    } catch {
      await new Promise((r) => setTimeout(r, 400));
      return keep(await this.loader!.loadAs(url, 'gltf') as { scene: THREE.Object3D; animations?: THREE.AnimationClip[] });
    }
  }

  /** S8 diagnostics (`__assetBytes()`). */
  assetBytesStats(): { entries: number; bytesMb: number; budgetMb: number; hits: number; misses: number; puts: number; evictions: number } {
    return { entries: this.bytesCache.size, bytesMb: +(this.bytesCache.bytes / 1048576).toFixed(1), budgetMb: BYTES_CACHE_BUDGET / 1048576, ...this.bytesCache.stats };
  }

  // Only hard-surface STRUCTURES are smoothed (the user's "buildings"). Foliage
  // (kind='foliage'), avatars, envmaps etc. keep their authored normals. Add
  // 'decor'/'prop' here if those should smooth too.
  private static readonly SMOOTH_KINDS = new Set<string>(['structure']);

  /** Smooth a freshly-loaded master in place, gated by the asset kind + active tier. */
  /** Material effects (shader wave stage 2): counts for `__materialFx()`. */
  static readonly materialFxStats = { accumulation: 0, physical: 0, iridescent: 0, scenes: 0 };
  private static readonly FX_KINDS = new Set<string>(['structure', 'prop', 'decor']);

  /**
   * Shader wave stage 2 — the engine's material layers on props/structures:
   * snow ACCUMULATION by world-up (engine AccumulationLayer; the weather's snow
   * uniform drives coverage through lib/wetness accumulationHandles), a
   * CLEARCOAT on metallic alloys and IRIDESCENCE on emissive bio-glass (three's
   * MeshPhysicalNodeMaterial, the same BSDF terms the engine Substrate exposes).
   * Parse-time: applies to newly loaded assets when `materialFx` is on.
   */
  private applyMaterialFx(scene: THREE.Object3D, kind: string): void {
    if (!AssetModule.FX_KINDS.has(kind) || !activePreset().materialFx) return;
    AssetModule.materialFxStats.scenes++;
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const out: THREE.Material[] = [];
      for (const mat0 of mats) {
        let mat = mat0 as THREE.MeshStandardMaterial & { userData: Record<string, unknown> };
        // The classic GLTFLoader hands out MeshStandardMaterial; three's WebGPURenderer
        // converts it to a node material at first render, copying custom node slots
        // (colorNode …) along — so the engine layers set here survive. Accept both.
        const flags = mat as { isMeshStandardMaterial?: boolean; isMeshStandardNodeMaterial?: boolean; isMeshPhysicalMaterial?: boolean; isMeshPhysicalNodeMaterial?: boolean };
        if (!(flags.isMeshStandardMaterial || flags.isMeshStandardNodeMaterial) || mat.userData.materialFx) { out.push(mat0); continue; }
        // Two INDEPENDENT try blocks (2026-09-14): the physical conversion used
        // to share one with the accumulation layer, so when it threw — which it
        // did for every classic GLTF material, see lib/material_fx.ts — the snow
        // layer was silently skipped for that material as well.
        try {
          const metal = mat.metalness ?? 0;
          const em = mat.emissive as THREE.Color | undefined;
          const emissiveTeal = !!em && em.g > 0.25 && em.b > 0.25 && em.r < Math.min(em.g, em.b) * 0.8;
          if (!(flags.isMeshPhysicalMaterial || flags.isMeshPhysicalNodeMaterial) && (metal >= 0.6 || emissiveTeal)) {
            const phys = toPhysicalMaterial(mat, !!flags.isMeshStandardNodeMaterial);
            if (metal >= 0.6) { phys.clearcoat = 0.3; phys.clearcoatRoughness = 0.25; AssetModule.materialFxStats.physical++; }
            if (emissiveTeal) { phys.iridescence = 0.55; phys.iridescenceIOR = 1.6; AssetModule.materialFxStats.iridescent++; }
            mat = phys as unknown as typeof mat;
          }
        } catch { /* best-effort: an unconvertible material stays standard */ }
        try {
          if ((mat.alphaTest ?? 0) === 0 && mat.transparent !== true) {
            const h = applyAccumulationLayer(mat as never, { color: [0.9, 0.93, 0.98], roughness: 0.85, coverage: 0, upBias: 0.35, noiseScale: 0.9, noiseStrength: 0.35 });
            accumulationHandles.add(h);
            AssetModule.materialFxStats.accumulation++;
          }
          mat.userData.materialFx = true;
        } catch { /* best-effort: no accumulation layer on this material */ }
        out.push(mat as unknown as THREE.Material);
      }
      m.material = Array.isArray(m.material) ? out : out[0];
    });
  }

  /**
   * Shader additions wave (2026-09-15), per material at parse — each behind
   * its own tier flag and INDEPENDENT of `materialFx` (specular AA is on from
   * low, where the material effects are not):
   *   glass        — prop / decor panes become real transmission (structures
   *                  keep interior mapping — glassTierFor says so)
   *   anisotropy   — metals brush (converted to physical if needed)
   *   retro        — signs and markers get the light-return lobe
   *   specular AA  — every standard/physical material, applied LAST so it
   *                  composes over the wetness / snow / detail roughness nodes
   * The four run in that order on purpose: the first two may swap the
   * material for its physical twin, and the last must patch the final one.
   */
  private applyStage5Materials(scene: THREE.Object3D, kind: string): void {
    const q = activePreset();
    if (!AssetModule.FX_KINDS.has(kind)) return;
    if (!(q.specularAA || q.glassTransmission || q.hardSurfaceAnisotropy || q.retroreflection)) return;
    const tier = activeTier();
    const cap = specularAAVarianceCap(tier);
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const out: THREE.Material[] = [];
      for (const mat0 of mats) {
        let mat = mat0 as THREE.MeshStandardMaterial & { userData: Record<string, unknown> };
        const flags = mat as { isMeshStandardMaterial?: boolean; isMeshStandardNodeMaterial?: boolean; isMeshPhysicalMaterial?: boolean; isMeshPhysicalNodeMaterial?: boolean };
        const standardish = !!(flags.isMeshStandardMaterial || flags.isMeshStandardNodeMaterial);
        if (!standardish || mat.userData.stage5) { out.push(mat0); continue; }
        const isPhysical = (): boolean => !!((mat as unknown as typeof flags).isMeshPhysicalMaterial || (mat as unknown as typeof flags).isMeshPhysicalNodeMaterial);
        const toPhys = (): void => { if (!isPhysical()) mat = toPhysicalMaterial(mat, !!flags.isMeshStandardNodeMaterial) as unknown as typeof mat; };
        try {
          const gt = q.glassTransmission ? glassTierFor(kind, mat.name) : null;
          if (gt) {
            toPhys();
            const tierCfg = gt === 'thick'
              ? { tier: 'thick' as const, thicknessM: 0.06, attenuationColor: [0.85, 0.95, 1.0] as [number, number, number], attenuationDistanceM: 0.4, dispersion: 0.05, roughness: Math.min(0.25, mat.roughness ?? 0.1) }
              : { tier: 'thin' as const, roughness: Math.min(0.2, mat.roughness ?? 0.05) };
            if (applyGlassTransmission(mat as never, tierCfg)) { if (gt === 'thick') stage5Stats.glassThick++; else stage5Stats.glassThin++; }
          }
        } catch { /* best-effort */ }
        try {
          const a = q.hardSurfaceAnisotropy ? anisotropyFor(mat.metalness ?? 0, mat.name) : null;
          if (a) { toPhys(); if (applyAnisotropy(mat as never, a)) stage5Stats.anisotropicMats++; }
        } catch { /* best-effort */ }
        try {
          const rs = q.retroreflection ? retroFor(mat.name) : 0;
          if (rs > 0) { retroHandles.add(applyRetroreflection(mat as never, { strength: rs }) as never); stage5Stats.retroMats++; }
        } catch { /* best-effort */ }
        try {
          if (q.specularAA) { specularAAHandles.add(applySpecularAA(mat as never, { varianceCap: cap })); stage5Stats.specularAAMats++; }
        } catch { /* best-effort */ }
        mat.userData.stage5 = true;
        out.push(mat as unknown as THREE.Material);
      }
      m.material = Array.isArray(m.material) ? out : out[0];
    });
  }

  /**
   * Shader wave stage 3 (2026-09-09): interior mapping on the window glass.
   * A cooked civ structure's panes are their own materials (`AS_FutureCity_
   * Glass`, `…GlassFacade` — the material-fx probe listed them); each is
   * replaced at parse by the engine's Van Dongen room shader (rooms behind
   * the glass from the tangent-space view ray; three r185 derives the frame
   * from screen derivatives when the geometry has no tangents), lit by night
   * through `litRatio` (AtlasShaderFxModule drives every handle). The
   * instancer's fade clone copies the node slots, so instanced facades keep it.
   */
  private applyInteriorMapping(scene: THREE.Object3D, kind: string): void {
    if (kind !== 'structure' || !activePreset().interiorMapping) return;
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      let changed = false;
      const out = mats.map((mat0) => {
        const name = (mat0 as THREE.Material).name;
        if (!isGlassMaterialName(name) || (mat0 as THREE.Material).userData.interior) return mat0;
        try {
          const rooms = roomsForGlass(name);
          const { material, uniforms } = createInteriorMappingMaterial({ ...rooms, litRatio: litRatioForNight(night), lightColor: [1.0, 0.86, 0.62], glassColor: [0.5, 0.62, 0.74], glassReflectivity: 0.28 });
          material.name = `${name}#interior`;
          material.side = (mat0 as THREE.Material).side; // a DoubleSide pane stays visible from inside the building
          material.userData.interior = true;
          interiorHandles.add(uniforms);
          stage3Stats.interior++;
          changed = true;
          return material as unknown as THREE.Material;
        } catch { return mat0; }
      });
      if (changed) m.material = Array.isArray(m.material) ? out : out[0];
    });
  }

  private async maybeSmooth(scene: THREE.Object3D, kind: string): Promise<void> {
    this.applyMaterialFx(scene, kind);
    this.applyStage5Materials(scene, kind);
    this.applyInteriorMapping(scene, kind);
    // Stage 4: cloud shadows on structures, props and decor (after the snow
    // layer so the shadow darkens the snow too; interior-mapped glass skipped).
    if (AssetModule.FX_KINDS.has(kind) && activePreset().cloudShadows) {
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        for (const mat of (Array.isArray(m.material) ? m.material : [m.material])) {
          const mm = mat as THREE.Material & { userData: Record<string, unknown>; transparent?: boolean };
          if (mm.userData.interior || mm.transparent) continue;
          applyCloudShadows(this.app ?? null, mm as never);
        }
      });
    }
    if (!AssetModule.SMOOTH_KINDS.has(kind)) return;
    const q = activePreset();
    if (q.meshSmoothing && localStorage.getItem('atlas:no-smooth') !== '1') {
      // Off-thread: the synchronous smoothModel here was ~8.4 s of main-thread
      // work per city stream-in (profiled 2026-07-20) — the area-entry lag.
      await smoothModelOffThread(scene, 50);
    }
    if (q.surfaceDetail) {
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        for (const mat of mats) {
          const mm = mat as unknown as { alphaTest?: number; transparent?: boolean };
          if ((mm.alphaTest ?? 0) === 0 && mm.transparent !== true) {
            // Footprint filtering (2026-09-15): the detail fades where its period
            // drops under a pixel and hands its slope energy to roughness.
            const band = detailFilterBand(activeTier());
            try { applyDetailSurface(mat as THREE.NodeMaterial, { tiling: 12, normalStrength: 0.3, roughnessBreakup: 0.12, filtered: !!q.proceduralFiltering, filterStart: band[0], filterEnd: band[1] }); if (q.proceduralFiltering) stage5Stats.filteredDetailMats++; }
            catch { /* best-effort; an unsupported material is left as-is */ }
          }
        }
      });
    }
  }

  init(app: App): void {
    // A dedicated ModelPipeline, KTX2-initialized with the app renderer — the same
    // fully-wired chain App builds for `app.gltf`, kept decoupled from engine internals.
    const loader = new ModelPipeline();
    // Share App.gltf's KTX2 transcoder: a second KTX2Loader is a second worker
    // pool and three warns about it ("Multiple active KTX2 loaders…", perf sweep 2026-08-22).
    loader.init(app.renderer.native, { ktx2Loader: (app as { gltf?: { ktx2?: unknown } }).gltf?.ktx2 as never ?? null });
    this.loader = loader;
    this.app = app;
    this.ktx2 = ((app as { gltf?: { ktx2?: unknown } }).gltf?.ktx2 ?? (loader as unknown as { gltf?: { ktx2?: unknown } }).gltf?.ktx2 ?? null) as { loadAsync(url: string): Promise<THREE.Texture> } | null;
    // L5: which raw (pre-transcoded) pack variant this GPU can sample.
    try { this.rawCodec = pickRawCodec(detectCodecSupport(app.renderer.native), rawCodecOptedOut()); } catch { this.rawCodec = null; }
    this.rawStats.codec = this.rawCodec ?? '';
    if (this.rawCodec) Logger.info('Assets', `raw texture packs: ${this.rawCodec}`);
  }

  /**
   * Tier prep for a freshly parsed master, BEFORE it can reach the renderer:
   * cap compressed mip chains to the tier's maxTextureRes (the 464 MB/48 s
   * potato-walk upload burst), pre-upload what remains via the budgeted warm
   * queue (first bind then finds the texture resident), and return the
   * MEASURED GPU footprint for cache accounting (the cooked estimate is
   * LOD0-based and over-counts LOD1 variants ~20× — budget-thrash).
   */
  /** First-upload mip cap (px) for mips-first uploads; the tier cap is promoted later. */
  static MIPS_FIRST_CAP = 512;
  private readonly mipPromoteQueue: Array<{ base: THREE.Texture; top: THREE.Texture }> = [];
  private mipPromoteTimer: number | null = null;
  readonly mipPromoteStats = { queued: 0, promoted: 0, failed: 0 };

  /** Build the "top" textures (levels above the first cap, down to the tier cap) and drain them one per task. */
  private scheduleMipPromotion(retained: RetainedTop[], tierCap: number): void {
    for (const r of retained) {
      // Keep only the levels the tier allows (the cut list is largest-first).
      const levels = r.topMips.filter((m) => Math.max(m.width, m.height) <= tierCap);
      if (levels.length === 0) continue;
      const base = r.texture as THREE.CompressedTexture;
      const top = new THREE.CompressedTexture(levels as never, levels[0].width, levels[0].height, base.format, base.type);
      top.colorSpace = base.colorSpace; top.wrapS = base.wrapS; top.wrapT = base.wrapT; top.anisotropy = base.anisotropy;
      top.minFilter = base.minFilter; top.magFilter = base.magFilter; top.generateMipmaps = false; top.flipY = base.flipY; top.needsUpdate = true;
      this.mipPromoteQueue.push({ base, top });
      this.mipPromoteStats.queued++;
    }
    if (this.mipPromoteTimer === null) this.mipPromoteTimer = setTimeout(() => this.drainMipPromotion(), 900) as unknown as number;
  }

  private drainMipPromotion(): void {
    this.mipPromoteTimer = null;
    const job = this.mipPromoteQueue.shift();
    if (!job) return;
    const native = this.app?.renderer.native as unknown;
    try {
      const r = promoteCompressedTexture(native, job.base, job.top);
      if (r === 'promoted' || r === 'already') this.mipPromoteStats.promoted++;
      else { this.mipPromoteStats.failed++; try { job.top.dispose(); } catch { /* never uploaded */ } }
    } catch { this.mipPromoteStats.failed++; }
    // One texture per task, a frame apart: the top levels of a skin are the
    // large bytes — spreading them is the point.
    if (this.mipPromoteQueue.length) this.mipPromoteTimer = setTimeout(() => this.drainMipPromotion(), 24) as unknown as number;
  }

  private tierPrep(scene: THREE.Object3D): number {
    const preset = activePreset();
    // MIPS-FIRST UPLOADS (perf frontier 2026-09-08, item 15): on the WebGPU
    // backend the first upload carries only the levels up to MIPS_FIRST_CAP —
    // the bytes that make the first draw — and the levels above are promoted
    // onto the resident texture with GPU copies a little later, one texture
    // per task, so a settlement streaming in never uploads its 2K skins in
    // the frame it appears. WebGL2 (no promotion) uploads the tier cap at once.
    const native = this.app?.renderer.native as unknown as { backend?: { isWebGPUBackend?: boolean } } | undefined;
    const mipsFirst = !!native?.backend?.isWebGPUBackend && preset.maxTextureRes > AssetModule.MIPS_FIRST_CAP && !mipsFirstOptedOut();
    const retained: RetainedTop[] = [];
    const report = mipsFirst ? capSceneTextures(scene, AssetModule.MIPS_FIRST_CAP, retained) : capSceneTextures(scene, preset.maxTextureRes);
    if (mipsFirst && retained.length) this.scheduleMipPromotion(retained, preset.maxTextureRes);
    // Anisotropy from the settings (menu wave): applied at parse; a quality
    // change re-streams via the StreamingModule evict, so it converges.
    const aniso = preset.anisotropy ?? 4;
    scene.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[] | undefined;
      for (const mat of Array.isArray(m) ? m : m ? [m] : []) {
        for (const key of ['map', 'normalMap', 'roughnessMap', 'aoMap'] as const) {
          const t = mat[key] as THREE.Texture | null;
          if (t && t.anisotropy !== aniso) { t.anisotropy = aniso; t.needsUpdate = true; }
        }
      }
    });
    if (this.app) queueTextureWarm(this.app, scene);
    return Math.max(0.5, report.measuredMb);
  }

  /**
   * Load a registered render asset by id. Cached by content hash, so a repeat load
   * serves the shared Object3D with NO second network fetch.
   */
  async load(renderAssetId: bigint): Promise<THREE.Object3D> {
    if (!this.loader) throw new Error('AssetModule not initialized');
    const row = this.resolve(renderAssetId);
    if (!row) throw new Error(`render_asset #${renderAssetId} not found in the registry`);
    const cached = this.cache.acquire(this.acquiredKey(row.contentHash));
    if (cached) return cached; // cache hit — no network fetch
    // Single-flight: concurrent loads of the same asset share ONE fetch+parse;
    // late joiners acquire their own cache ref once the first put() lands.
    const flying = this.inflight.get(row.contentHash);
    if (flying) { await flying.catch(() => { /* first attempt failed */ }); return this.load(renderAssetId); }
    const rep = this.representation(renderAssetId, 0, row.contentHash, row.sourceUri);
    const p = this.gate(async () => {
      const again = this.cache.acquire(rep.key); // raced another load while queued
      if (again) return again;
      const scene = await this.fetchScene(rep.uri);
      shareSceneTextures(scene); // S4: one GPU texture per distinct KTX2 image across levels
      await this.maybeSmooth(scene, row.kind); // buildings-only, tier-gated; clones inherit
      return this.cache.put(rep.key, scene, this.tierPrep(scene)); // measured VRAM, not the LOD0 estimate
    });
    this.inflight.set(row.contentHash, p);
    try { return await p; } finally { this.inflight.delete(row.contentHash); }
  }

  /**
   * G5: warm the cache for `renderAssetId` WITHOUT holding a reference (load then release →
   * the entry sits at refcount 0, evictable, ready for an instant cache hit when the region
   * arrives). Idempotent + cheap on a hit. Errors are swallowed (prefetch is best-effort).
   */
  async prefetch(renderAssetId: bigint): Promise<void> {
    try {
      const row = this.resolve(renderAssetId);
      if (!row) return;
      await this.load(renderAssetId);
      this.cache.release(this.acquiredKey(row.contentHash)); // leave it cache-warm at refcount 0
    } catch { /* prefetch is best-effort */ }
  }

  /** G5: total resident VRAM estimate (MB) of cached GLB assets. */
  residentVramMb(): number { return this.cache.residentVramMb(); }

  /** G5: LRU-evict cache-warm assets until under the budget; returns the count evicted. */
  evictToBudget(budgetMb: number): number { return this.cache.evictToBudget(budgetMb); }

  /** G5: how many cache-warm (refcount-0) assets are currently evictable. */
  unreferencedCount(): number { return this.cache.unreferencedCount(); }

  /** G5 test hook: the cache refcount for an asset (by its content hash). */
  cacheRefs(renderAssetId: bigint): number { const row = this.resolve(renderAssetId); return row ? this.cache.refs(this.acquiredKey(row.contentHash)) : 0; }

  /**
   * Load a prefiltered-HDRI envmap (G3, kind=envmap) as an equirectangular DataTexture.
   * Parses the `.hdr` via three's HDRLoader, then REBUILDS the data into a `three/webgpu`
   * DataTexture (the renderer + PMREMGenerator use that build — not the `three` one the
   * addon loader emits). Cached by content hash; the caller PMREM-prefilters it.
   */
  async loadEnvMap(renderAssetId: bigint): Promise<THREE.DataTexture> {
    const row = this.resolve(renderAssetId);
    if (!row) throw new Error(`render_asset #${renderAssetId} (envmap) not found in the registry`);
    const cached = this.envCache.get(row.contentHash);
    if (cached) return cached;
    const buf = await (await fetch(`${ASSET_STORE_BASE}/${row.sourceUri}`)).arrayBuffer();
    const hdr = new HDRLoader().setDataType(THREE.FloatType).parse(buf);
    const tex = new THREE.DataTexture(hdr.data, hdr.width, hdr.height, THREE.RGBAFormat, THREE.FloatType);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.LinearSRGBColorSpace; // HDR radiance is linear
    tex.needsUpdate = true;
    this.envCache.set(row.contentHash, tex);
    return tex;
  }

  /**
   * Load a ground/facade texture directly by its MinIO key (e.g. 'textures/regolith_ground.png').
   * Tiled sRGB albedo, cached by key. Textures are CDN assets, not render_asset rows.
   */
  async loadTexture(key: string, srgb = true): Promise<THREE.Texture> {
    const cached = this.textureCache.get(key);
    if (cached) return cached;
    // Single-flight: the same key requested twice (regolith is both the
    // personal ground and a Commons biome texture) decoded twice and orphaned
    // the first texture, handing two materials different Texture objects.
    const inflight = this.textureInflight.get(key);
    if (inflight) return inflight;
    const p = (async () => {
      const tex = await new THREE.TextureLoader().loadAsync(`${ASSET_STORE_BASE}/${key}`);
      tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping; // tile across the terrain (else clamp + smear)
      tex.generateMipmaps = true;
      tex.needsUpdate = true;
      this.textureCache.set(key, tex);
      return tex;
    })();
    this.textureInflight.set(key, p);
    try { return await p; } finally { this.textureInflight.delete(key); }
  }

  /**
   * Release one load. With no argument this is the IModule teardown (disposes the
   * whole cache + loader); with an id it releases that asset, freeing GPU memory
   * when the last reference drops.
   */
  dispose(renderAssetId?: bigint): void {
    if (renderAssetId === undefined) {
      this.cache.clear();
      for (const t of this.envCache.values()) t.dispose();
      this.envCache.clear();
      for (const t of this.textureCache.values()) t.dispose();
      this.textureCache.clear();
      this.loader?.dispose();
      this.loader = null;
      return;
    }
    const row = this.resolve(renderAssetId);
    if (row) this.cache.release(this.acquiredKey(row.contentHash));
  }

  /** Load a specific LOD variant's GLB (cached by its uri); falls back to LOD0 (G2). */
  async loadVariant(renderAssetId: bigint, lod: number): Promise<THREE.Object3D> {
    if (!this.loader) throw new Error('AssetModule not initialized');
    if (lod === 0) return this.load(renderAssetId);
    const v = this.variant(renderAssetId, lod);
    if (!v) return this.load(renderAssetId); // no such LOD → the canonical model
    const cached = this.cache.acquire(this.acquiredKey(v.uri));
    if (cached) return cached;
    // Single-flight (same as load()): two concurrent loads of one variant used
    // to fetch+parse it twice, with the loser disposed at put() (sweep-confirmed).
    const flying = this.inflight.get(v.uri);
    if (flying) { await flying.catch(() => { /* first attempt failed */ }); return this.loadVariant(renderAssetId, lod); }
    const row = this.resolve(renderAssetId);
    const rep = this.representation(renderAssetId, lod, v.uri, v.uri);
    const p = this.gate(async () => {
      const again = this.cache.acquire(rep.key); // raced another loadVariant while queued
      if (again) return again;
      const scene = await this.fetchScene(rep.uri);
      shareSceneTextures(scene); // S4
      await this.maybeSmooth(scene, row?.kind ?? ''); // '' → not smoothed
      return this.cache.put(rep.key, scene, this.tierPrep(scene)); // measured VRAM, not a poly-scaled LOD0 estimate
    });
    this.inflight.set(v.uri, p);
    try { return await p; } finally { this.inflight.delete(v.uri); }
  }

  /**
   * Load the representation the ACTIVE TIER will actually render: on
   * forceLowLod tiers (potato) a cooked LOD1 variant when one exists, else the
   * canonical LOD0. Returns the object plus a release closure matched to
   * whichever cache key was actually acquired — callers must store and call it
   * instead of guessing dispose vs disposeVariant (a mismatched release leaks
   * the refcount forever).
   */
  async loadForTier(renderAssetId: bigint): Promise<{ object: THREE.Object3D; release: () => void }> {
    return this.loadRepresentation(renderAssetId, activePreset().settlementForceLowLod ? 1 : 0);
  }

  /** Load a specific LOD when its variant row exists, else the canonical —
   *  with a release closure matched to whichever cache key was acquired. */
  async loadRepresentation(renderAssetId: bigint, lod: number): Promise<{ object: THREE.Object3D; release: () => void; metrics?: LoadMetrics }> {
    // Nearest LOWER level that exists (3 → 2 → 1 → 0): a missing row used to
    // fall straight to the full-detail LOD0 (civb_city5 had no LOD2, so the
    // low tiers' far ring loaded its 40k-triangle file — the opposite of the
    // intent). The release closure is bound to the level actually acquired.
    const eff = this.resolveLod(renderAssetId, lod);
    const useVariant = eff > 0;
    const object = useVariant ? await this.loadVariant(renderAssetId, eff) : await this.load(renderAssetId);
    const release = useVariant
      ? () => this.disposeVariant(renderAssetId, eff)
      : () => this.dispose(renderAssetId);
    const uri = useVariant ? this.variant(renderAssetId, eff)?.uri : this.resolve(renderAssetId)?.sourceUri;
    return { object, release, metrics: (uri && this.takeLoadMetrics(uri)) || AssetModule.NO_METRICS };
  }

  /**
   * Load the canonical (LOD0) model under an explicit texture cap — the
   * per-building refinement's path (R8): its LOD0 files are 1024 px on every
   * preset but ultra. Keyed by the tier file itself (NOT through tierKeyFor:
   * two callers wanting different caps of one asset must not overwrite each
   * other's release key), single-flight, with a release bound to that key.
   */
  async loadTiered(renderAssetId: bigint, maxTextureRes: number): Promise<{ object: THREE.Object3D; release: () => void; metrics?: LoadMetrics }> {
    if (!this.loader) throw new Error('AssetModule not initialized');
    const row = this.resolve(renderAssetId);
    if (!row) throw new Error(`render_asset #${renderAssetId} not found in the registry`);
    const tier = atlas.conn && !textureTiersOptedOut()
      ? pickTextureTier(AssetModule.rowsByAsset<{ renderAssetId: bigint; lod: number; uri: string; maxTextureRes: number; sizeBytes: bigint }>(
        (atlas.conn.db as unknown as Record<string, unknown>).renderAssetTextureTier, 'renderAssetTextureTierByAsset', renderAssetId), 0, maxTextureRes)
      : undefined;
    const uri = tier && tier.uri !== row.sourceUri ? tier.uri : row.sourceUri;
    const key = tier && tier.uri !== row.sourceUri ? tier.uri : row.contentHash;
    const release = (): void => { this.cache.release(key); };
    const cached = this.cache.acquire(key);
    if (cached) return { object: cached, release, metrics: AssetModule.NO_METRICS };
    const flying = this.inflight.get(key);
    if (flying) { await flying.catch(() => { /* first attempt failed */ }); return this.loadTiered(renderAssetId, maxTextureRes); }
    const p = this.gate(async () => {
      const again = this.cache.acquire(key);
      if (again) return again;
      const scene = await this.fetchScene(uri);
      shareSceneTextures(scene); // S4
      await this.maybeSmooth(scene, row.kind);
      return this.cache.put(key, scene, this.tierPrep(scene));
    });
    this.inflight.set(key, p);
    try { const object = await p; return { object, release, metrics: this.takeLoadMetrics(uri) ?? AssetModule.NO_METRICS }; } finally { this.inflight.delete(key); }
  }

  /** The highest LOD level ≤ `lod` that has a cooked variant row (0 = the canonical model, always available). */
  resolveLod(renderAssetId: bigint, lod: number): number {
    // One indexed lookup (the btree accessor), not one per level (R10).
    const have = new Set(this.variants(renderAssetId).map((v) => v.lod));
    for (let l = Math.floor(lod); l > 0; l--) if (have.has(l)) return l;
    return 0;
  }

  /** Release a previously-loaded LOD variant (by its uri). */
  disposeVariant(renderAssetId: bigint, lod: number): void {
    if (lod === 0) { this.dispose(renderAssetId); return; }
    const v = this.variant(renderAssetId, lod);
    if (v) this.cache.release(this.acquiredKey(v.uri));
  }

  /** The LOD-variant rows for an asset, sorted by lod (0..N). */
  variants(renderAssetId: bigint): { lod: number; uri: string; polyCount: number; screenSizeThreshold: number }[] {
    const conn = atlas.conn;
    if (!conn) return [];
    return AssetModule.rowsByAsset<{ renderAssetId: bigint; lod: number; uri: string; polyCount: number; screenSizeThreshold: number }>(
      conn.db.renderAssetVariant, 'renderAssetVariantByAsset', renderAssetId).sort((a, b) => a.lod - b.lod);
  }

  /** True when a cooked variant row exists for (asset, lod) — i.e. loadRepresentation(lod) acquires the VARIANT key. */
  hasVariant(renderAssetId: bigint, lod: number): boolean { return this.variant(renderAssetId, lod) !== undefined; }

  private variant(renderAssetId: bigint, lod: number) {
    // Indexed lookup — this used to spread the whole ~800-row table per call,
    // three calls per prefab per settlement build (settlement study F8).
    if (!atlas.conn) return undefined;
    return AssetModule.rowsByAsset<{ renderAssetId: bigint; lod: number; uri: string; polyCount: number; screenSizeThreshold: number }>(
      atlas.conn.db.renderAssetVariant, 'renderAssetVariantByAsset', renderAssetId).find((x) => x.lod === lod);
  }

  /** Texture-share registry (probe: `__textureShare()`). */
  textureShareDiag(): ReturnType<typeof textureShareDiag> { return textureShareDiag(); }

  /** True if the asset's content hash is currently cached (the next load won't fetch). */
  isCached(renderAssetId: bigint): boolean {
    const row = this.resolve(renderAssetId);
    return row ? this.cache.has(this.acquiredKey(row.contentHash)) : false;
  }

  private resolve(renderAssetId: bigint) {
    return atlas.conn?.db.renderAsset.renderAssetId.find(renderAssetId);
  }
}
